import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Camera, MapPin, Hand, ArrowRight } from "lucide-react";
import ActivityTicker from "@/components/ActivityTicker";
import NearbyCounter from "@/components/NearbyCounter";

const HERO_IMG = "https://images.pexels.com/photos/28385865/pexels-photo-28385865.jpeg";
const ITEM_IMG_1 = "https://images.pexels.com/photos/24994303/pexels-photo-24994303.jpeg";
const ITEM_IMG_2 = "https://images.pexels.com/photos/27686655/pexels-photo-27686655.jpeg";
const ITEM_IMG_3 = "https://images.pexels.com/photos/30506001/pexels-photo-30506001.jpeg";

export default function Landing() {
  return (
    <div className="">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 fade-in-up">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#0F0F12] font-medium mb-5"
              data-testid="hero-tag">A neighbourhood good-stuff exchange</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.02] font-medium">
              That chair on the curb? <span className="text-[#7C3AED]">Someone wants it.</span>
            </h1>
            <p className="mt-6 text-lg text-[#27272A] max-w-xl leading-relaxed">
              Snap a photo of anything you're leaving out for free. Your neighbours see it
              on a map and swing by to give it a second life. No money, no shipping — just curb to home.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg"
                className="rounded-full bg-[#7C3AED] hover:bg-[#5B21B6] text-white px-7 h-12 text-base"
                data-testid="hero-post-btn">
                <Link to="/post"><Camera className="h-5 w-5 mr-2" />Post a free item</Link>
              </Button>
              <Button asChild size="lg" variant="outline"
                className="rounded-full border-[#0F0F12] text-[#0F0F12] hover:bg-[#0F0F12] hover:text-white px-7 h-12 text-base"
                data-testid="hero-browse-btn">
                <Link to="/browse"><MapPin className="h-5 w-5 mr-2" />Browse the curb</Link>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <ActivityTicker />
              <NearbyCounter />
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#6B7280]">
              <div><strong className="text-[#0F0F12] font-display text-2xl">100%</strong><div>Free, always</div></div>
              <div className="h-8 w-px bg-[#E8E5F0]" />
              <div><strong className="text-[#0F0F12] font-display text-2xl">5</strong><div>Photos per post</div></div>
              <div className="h-8 w-px bg-[#E8E5F0]" />
              <div><strong className="text-[#0F0F12] font-display text-2xl">∞</strong><div>Things saved from landfill</div></div>
            </div>
          </div>

          {/* Bento image cluster */}
          <div className="lg:col-span-5 grid grid-cols-6 grid-rows-6 gap-3 h-[460px] sm:h-[560px]">
            <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden bg-[#E8E5F0]">
              <img src={HERO_IMG} alt="Quiet residential street" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden bg-[#E8E5F0]">
              <img src={ITEM_IMG_1} alt="Blue sofa on the curb" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden bg-[#E8E5F0]">
              <img src={ITEM_IMG_2} alt="Vintage chair" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden bg-[#E8E5F0]">
              <img src={ITEM_IMG_3} alt="Wooden chairs" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-white border-y border-[#E8E5F0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#7C3AED] font-medium">How it works</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-medium mt-3">
              Three steps from your curb to your neighbour's living room.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Camera, num: "01", title: "Snap & post",
                desc: "Take up to 5 photos of the item you're leaving out. Add a quick title and category." },
              { icon: MapPin, num: "02", title: "Drop a pin",
                desc: "Mark where the item is — your address or a corner — so locals can find it easily." },
              { icon: Hand, num: "03", title: "Someone takes it home",
                desc: "Neighbours browse the live map, swing by, and mark it claimed. Less waste, more delight." },
            ].map((s) => (
              <div key={s.num}
                className="p-7 rounded-3xl bg-[#F7F5FB] border border-[#E8E5F0] transition-all hover:-translate-y-1 hover:shadow-md">
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-full bg-[#7C3AED]/10 grid place-items-center text-[#7C3AED]">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="font-display text-3xl text-[#E8E5F0] font-medium">{s.num}</span>
                </div>
                <h3 className="font-display text-xl mt-6 font-medium tracking-tight">{s.title}</h3>
                <p className="text-[#6B7280] mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Button asChild className="rounded-full bg-[#0F0F12] text-white hover:bg-black h-12 px-6" data-testid="how-cta">
              <Link to="/browse">See what's on the curb near you <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-5 sm:px-8 py-10 text-sm text-[#6B7280] flex flex-wrap items-center justify-between gap-4">
        <div>© {new Date().getFullYear()} curbside · A friendlier way to share what you no longer need.</div>
        <div className="flex gap-5">
          <Link to="/browse" className="hover:text-[#7C3AED]">Browse</Link>
          <Link to="/post" className="hover:text-[#7C3AED]">Post</Link>
        </div>
      </footer>
    </div>
  );
}
